/* ==========================================================================
   HODL Vault – Startseite Konzept C
   Einblenden, Entschluesselungs-Effekt, Netzwerk-Canvas, 3D-Neigung,
   Wallet-Finder, Sicherheitskette, Nachweis-Ledger, Video, Block-Navigation.
   Ohne externe Bibliotheken. prefers-reduced-motion schaltet Bewegung ab.
   ========================================================================== */
(() => {
  if (window.VaultHomeLoaded) return;
  window.VaultHomeLoaded = true;

  const html = document.documentElement;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  html.classList.add('v-js');
  if (!reduce) html.classList.add('v-anim');

  const once = (el, key) => {
    if (!el || el.dataset[key]) return false;
    el.dataset[key] = '1';
    return true;
  };

  const inView = (el, cb, options = {}) => {
    if (!('IntersectionObserver' in window)) {
      cb(true);
      return null;
    }
    const io = new IntersectionObserver((entries) => entries.forEach((e) => cb(e.isIntersecting, e)), options);
    io.observe(el);
    return io;
  };

  /* --- Einblenden ------------------------------------------------------ */
  let revealObserver = null;
  const initReveal = (scope) => {
    const items = scope.querySelectorAll('[data-v-reveal]');
    if (reduce || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-in'));
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-in');
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      );
    }
    items.forEach((el) => {
      if (once(el, 'vRev')) revealObserver.observe(el);
    });
  };

  /* --- Entschluesselungs-Effekt der Hero-Zeilen ------------------------ */
  const GLYPHS = 'abcdefghjkmnopqrstuvwxyz0123456789#$%&*+=/<>';
  const scramble = (line, delay) => {
    const txt = line.querySelector('.vhero__txt');
    if (!txt) return;
    const target = txt.textContent;
    // Die Zeichenschicht liegt ausserhalb der H1, damit Suchmaschinen und
    // Screenreader in der Ueberschrift nie Zufallszeichen sehen.
    const host = line.closest('.vhero__text') || line.parentElement;
    const overlay = document.createElement('span');
    overlay.className = line.classList.contains('vhero__line--grad') ? 'v-scr v-scr--grad' : 'v-scr';
    overlay.setAttribute('aria-hidden', 'true');
    const place = () => {
      const hr = host.getBoundingClientRect();
      const lr = line.getBoundingClientRect();
      const tr = txt.getBoundingClientRect();
      const cs = getComputedStyle(txt);
      Object.assign(overlay.style, {
        left: `${tr.left - hr.left}px`,
        top: `${tr.top - hr.top}px`,
        width: `${lr.right - tr.left}px`,
        fontFamily: cs.fontFamily,
        fontSize: cs.fontSize,
        fontWeight: cs.fontWeight,
        letterSpacing: cs.letterSpacing,
        lineHeight: cs.lineHeight,
        whiteSpace: getComputedStyle(line).whiteSpace,
      });
    };
    host.appendChild(overlay);
    place();
    line.classList.add('is-scrambling');

    const duration = 900 + target.length * 22;
    const reveal = Array.from(target).map((ch, i) => (ch === ' ' ? 0 : (i / target.length) * duration * 0.72 + Math.random() * duration * 0.28));
    const safe = (g) => (g === '<' ? '&lt;' : g === '>' ? '&gt;' : g === '&' ? '&amp;' : g);
    overlay.innerHTML = Array.from(target)
      .map((ch) => (ch === ' ' ? ' ' : `<b>${safe(GLYPHS[(Math.random() * GLYPHS.length) | 0])}</b>`))
      .join('');
    let start = null;
    let last = 0;

    const frame = (t) => {
      if (start === null) start = t + delay;
      const elapsed = t - start;
      if (elapsed < 0) {
        requestAnimationFrame(frame);
        return;
      }
      if (t - last > 32) {
        last = t;
        let out = '';
        let done = true;
        for (let i = 0; i < target.length; i += 1) {
          const ch = target[i];
          if (ch === ' ' || elapsed >= reveal[i]) {
            out += ch === '<' ? '&lt;' : ch === '&' ? '&amp;' : ch;
          } else {
            done = false;
            const g = GLYPHS[(Math.random() * GLYPHS.length) | 0];
            out += `<b>${g === '<' ? '&lt;' : g === '>' ? '&gt;' : g === '&' ? '&amp;' : g}</b>`;
          }
        }
        overlay.innerHTML = out;
        if (done) {
          line.classList.remove('is-scrambling');
          line.classList.add('is-done');
          overlay.remove();
          return;
        }
      }
      requestAnimationFrame(frame);
    };
    requestAnimationFrame(frame);
  };

  const initScramble = (scope) => {
    if (reduce) return;
    scope.querySelectorAll('[data-v-scramble]').forEach((line, i) => {
      if (once(line, 'vScr')) scramble(line, 180 + i * 260);
    });
  };

  /* --- 3D-Neigung des Hero-Visuals ------------------------------------- */
  const initTilt = (scope) => {
    if (reduce || !finePointer) return;
    scope.querySelectorAll('[data-v-tilt]').forEach((wrap) => {
      if (!once(wrap, 'vTilt')) return;
      const stage = wrap.querySelector('[data-v-stage]') || wrap;
      let raf = 0;
      wrap.addEventListener('pointermove', (e) => {
        const r = wrap.getBoundingClientRect();
        const x = (e.clientX - r.left) / r.width - 0.5;
        const y = (e.clientY - r.top) / r.height - 0.5;
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          stage.style.setProperty('--ry', `${(x * 12).toFixed(2)}deg`);
          stage.style.setProperty('--rx', `${(-y * 10).toFixed(2)}deg`);
        });
      });
      wrap.addEventListener('pointerleave', () => {
        cancelAnimationFrame(raf);
        stage.style.setProperty('--ry', '0deg');
        stage.style.setProperty('--rx', '0deg');
      });
    });
  };

  /* --- Netzwerk-Canvas (Knoten und Verbindungen) ----------------------- */
  const initNet = (scope) => {
    if (reduce) return;
    scope.querySelectorAll('[data-v-net]').forEach((canvas) => {
      if (!once(canvas, 'vNet')) return;
      const start = () => runNet(canvas);
      if ('requestIdleCallback' in window) requestIdleCallback(start, { timeout: 1800 });
      else setTimeout(start, 900);
    });
  };

  const runNet = (canvas) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    const small = window.innerWidth < 750;
    const count = small ? 26 : 46;
    const linkDist = small ? 92 : 128;
    let w = 0;
    let h = 0;
    let nodes = [];
    let running = false;
    let visible = false;
    let raf = 0;
    let lastT = 0;
    const mouse = { x: -9999, y: -9999 };

    const resize = () => {
      const r = canvas.getBoundingClientRect();
      w = r.width;
      h = r.height;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (!nodes.length) {
        nodes = Array.from({ length: count }, () => ({
          x: Math.random() * w,
          y: Math.random() * h,
          vx: (Math.random() - 0.5) * 0.22,
          vy: (Math.random() - 0.5) * 0.22,
          r: Math.random() * 1.4 + 0.8,
          pulse: Math.random() < 0.18 ? Math.random() * Math.PI * 2 : -1,
        }));
      }
    };

    const step = (t) => {
      raf = requestAnimationFrame(step);
      if (t - lastT < 33) return;
      lastT = t;
      ctx.clearRect(0, 0, w, h);
      const cx = w / 2;
      const cy = h / 2;
      const fade = Math.min(w, h) * 0.52;

      for (let i = 0; i < nodes.length; i += 1) {
        const n = nodes[i];
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0 || n.x > w) n.vx *= -1;
        if (n.y < 0 || n.y > h) n.vy *= -1;
      }

      for (let i = 0; i < nodes.length; i += 1) {
        const a = nodes[i];
        for (let j = i + 1; j < nodes.length; j += 1) {
          const b = nodes[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d > linkDist) continue;
          const mx = (a.x + b.x) / 2 - mouse.x;
          const my = (a.y + b.y) / 2 - mouse.y;
          const near = Math.max(0, 1 - Math.sqrt(mx * mx + my * my) / 180);
          const edge = Math.max(0, 1 - Math.hypot((a.x + b.x) / 2 - cx, (a.y + b.y) / 2 - cy) / fade);
          const alpha = (1 - d / linkDist) * (0.12 + edge * 0.28 + near * 0.45);
          ctx.strokeStyle = `rgba(206, 38, 217, ${alpha.toFixed(3)})`;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        }
      }

      for (let i = 0; i < nodes.length; i += 1) {
        const n = nodes[i];
        const edge = Math.max(0, 1 - Math.hypot(n.x - cx, n.y - cy) / fade);
        let glow = 0.25 + edge * 0.6;
        if (n.pulse >= 0) {
          n.pulse += 0.05;
          glow += (Math.sin(n.pulse) + 1) * 0.3;
        }
        ctx.fillStyle = n.pulse >= 0 ? `rgba(192, 73, 233, ${Math.min(1, glow).toFixed(3)})` : `rgba(226, 180, 255, ${Math.min(1, glow).toFixed(3)})`;
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    const play = () => {
      if (running || !visible || document.hidden) return;
      running = true;
      raf = requestAnimationFrame(step);
    };
    const pause = () => {
      running = false;
      cancelAnimationFrame(raf);
    };

    resize();
    canvas.classList.add('is-on');
    if ('ResizeObserver' in window) new ResizeObserver(resize).observe(canvas);
    inView(canvas, (vis) => {
      visible = vis;
      if (vis) play();
      else pause();
    });
    document.addEventListener('visibilitychange', () => (document.hidden ? pause() : play()));

    const host = canvas.closest('[data-v-tilt]') || canvas.parentElement;
    if (finePointer && host) {
      host.addEventListener('pointermove', (e) => {
        const r = canvas.getBoundingClientRect();
        mouse.x = e.clientX - r.left;
        mouse.y = e.clientY - r.top;
      });
      host.addEventListener('pointerleave', () => {
        mouse.x = -9999;
        mouse.y = -9999;
      });
    }
  };

  /* --- Lichtkegel und Neigung auf Karten ------------------------------- */
  const initCards = (scope) => {
    if (!finePointer) return;
    scope.querySelectorAll('.vcard, .vtile').forEach((card) => {
      if (!once(card, 'vCard')) return;
      const tilt = card.classList.contains('vcard') && !reduce;
      let raf = 0;
      card.addEventListener('pointermove', (e) => {
        const r = card.getBoundingClientRect();
        const x = e.clientX - r.left;
        const y = e.clientY - r.top;
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          card.style.setProperty('--mx', `${x}px`);
          card.style.setProperty('--my', `${y}px`);
          if (tilt) {
            card.style.setProperty('--ry', `${((x / r.width - 0.5) * 6).toFixed(2)}deg`);
            card.style.setProperty('--rx', `${((0.5 - y / r.height) * 6).toFixed(2)}deg`);
          }
        });
      });
      card.addEventListener('pointerleave', () => {
        cancelAnimationFrame(raf);
        card.style.setProperty('--rx', '0deg');
        card.style.setProperty('--ry', '0deg');
      });
    });
  };

  /* --- Magnetische Knoepfe -------------------------------------------- */
  const initMagnet = (scope) => {
    if (reduce || !finePointer) return;
    scope.querySelectorAll('[data-v-magnet]').forEach((btn) => {
      if (!once(btn, 'vMag')) return;
      btn.addEventListener('pointermove', (e) => {
        const r = btn.getBoundingClientRect();
        const x = (e.clientX - r.left - r.width / 2) / (r.width / 2);
        const y = (e.clientY - r.top - r.height / 2) / (r.height / 2);
        btn.style.transform = `translate(${(x * 5).toFixed(1)}px, ${(y * 4).toFixed(1)}px)`;
      });
      btn.addEventListener('pointerleave', () => {
        btn.style.transform = '';
      });
    });
  };

  /* --- Wallet-Finder --------------------------------------------------- */
  const FLOW = {
    q1: (a) => (a.q1 === 'backup' ? 'q2-backup' : a.q1 === 'gift' ? 'q2-gift' : 'q2-wallet'),
    'q2-wallet': () => 'q3-wallet',
    'q2-backup': () => 'q3-backup',
    'q2-gift': () => 'q3-gift',
    'q3-wallet': () => null,
    'q3-backup': () => null,
    'q3-gift': () => null,
  };

  const decide = (a) => {
    let main = 'tangem';
    let extra = null;
    if (a.q1 === 'btc' || a.q1 === 'multi') {
      if (a.q2w === 'control') main = a.q1 === 'btc' ? 'bitbox_btc' : 'bitbox_multi';
      else if (a.q2w === 'split') main = 'cypherock';
      else main = 'tangem';
      if (a.q3w === 'none') extra = { tangem: 'safepaper', bitbox_btc: 'goldenseed', bitbox_multi: 'goldenseed', cypherock: 'phoenix' }[main];
    } else if (a.q1 === 'backup') {
      main = { paper: 'safepaper', metal: 'goldenseed', kit: 'phoenix' }[a.q2b] || 'safepaper';
      if (a.q3b === 'no') extra = 'tangem';
    } else if (a.q1 === 'gift') {
      main = a.q2g === 'play' ? 'game' : 'tangem';
      if (a.q3g === 'yes') extra = 'safepaper';
    }
    return { main, extra };
  };

  const initFinder = (scope) => {
    scope.querySelectorAll('[data-v-finder]').forEach((root) => {
      if (!once(root, 'vFind')) return;
      const form = root.querySelector('[data-v-finder-form]');
      const steps = Array.from(root.querySelectorAll('[data-step]'));
      const bars = Array.from(root.querySelectorAll('.vfind__progress span'));
      const label = root.querySelector('[data-v-step-label]');
      const back = root.querySelector('[data-v-back]');
      const next = root.querySelector('[data-v-next]');
      const nav = root.querySelector('.vfind__nav');
      const result = root.querySelector('[data-v-result]');
      const answers = {};
      const history = ['q1'];
      let pointerPick = false;
      let advanceTimer = null;

      const stepEl = (id) => steps.find((s) => s.dataset.step === id);
      const current = () => history[history.length - 1];

      const show = (id, focus) => {
        steps.forEach((s) => s.classList.toggle('is-active', s.dataset.step === id));
        const idx = history.length;
        bars.forEach((b, i) => b.classList.toggle('is-done', i < idx - 1));
        if (label) label.textContent = `Schritt ${idx} von 3`;
        if (back) back.hidden = idx <= 1;
        const el = stepEl(id);
        const checked = el && el.querySelector('input:checked');
        if (next) next.disabled = !checked;
        if (focus && el) {
          const first = el.querySelector('input:checked') || el.querySelector('input');
          if (first) first.focus({ preventScroll: true });
        }
      };

      const render = () => {
        const { main, extra } = decide(answers);
        const tpl = root.querySelector(`template[data-v-profile="${main}"]`) || root.querySelector('template[data-v-profile]');
        if (!tpl) return;
        result.innerHTML = '';
        result.appendChild(tpl.content.cloneNode(true));
        const slot = result.querySelector('[data-v-extra]');
        if (slot && extra && extra !== main) {
          const mini = root.querySelector(`template[data-v-mini="${extra}"]`);
          if (mini) slot.appendChild(mini.content.cloneNode(true));
        }
        const summary = result.querySelector('[data-v-summary]');
        if (summary) {
          const picked = Array.from(form.querySelectorAll('input:checked'))
            .filter((i) => i.closest('[data-step]') && history.includes(i.closest('[data-step]').dataset.step))
            .map((i) => i.dataset.short || '')
            .filter(Boolean);
          summary.textContent = picked.join(' · ');
        }
        steps.forEach((s) => s.classList.remove('is-active'));
        if (nav) nav.hidden = true;
        bars.forEach((b) => b.classList.add('is-done'));
        if (label) label.textContent = 'Ergebnis';
        result.hidden = false;
        const head = result.querySelector('[data-v-result-head]');
        if (head) head.focus({ preventScroll: true });
        const r = root.getBoundingClientRect();
        if (r.top < 0 || r.top > window.innerHeight * 0.4) {
          root.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
        }
        initCards(result);
      };

      const advance = () => {
        const id = current();
        const el = stepEl(id);
        const checked = el && el.querySelector('input:checked');
        if (!checked) return;
        answers[checked.name] = checked.value;
        const nextId = FLOW[id] ? FLOW[id](answers) : null;
        if (nextId) {
          history.push(nextId);
          show(nextId, !pointerPick);
        } else {
          render();
        }
      };

      form.addEventListener('pointerdown', () => {
        pointerPick = true;
      });
      form.addEventListener('keydown', () => {
        pointerPick = false;
      });
      form.addEventListener('change', (e) => {
        if (!e.target.matches('input[type="radio"]')) return;
        if (next) next.disabled = false;
        if (!pointerPick) return;
        clearTimeout(advanceTimer);
        advanceTimer = setTimeout(advance, reduce ? 0 : 280);
      });
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        clearTimeout(advanceTimer);
        advance();
      });
      if (back) {
        back.addEventListener('click', () => {
          if (history.length <= 1) return;
          history.pop();
          show(current(), true);
        });
      }
      root.addEventListener('click', (e) => {
        if (!e.target.closest('[data-v-restart]')) return;
        form.reset();
        Object.keys(answers).forEach((k) => delete answers[k]);
        history.length = 0;
        history.push('q1');
        result.hidden = true;
        result.innerHTML = '';
        if (nav) nav.hidden = false;
        show('q1', true);
      });

      show('q1', false);
    });
  };

  /* --- Sicherheitskette: Fortschritt beim Scrollen --------------------- */
  const chains = [];
  let chainTicking = false;
  const updateChains = () => {
    chainTicking = false;
    const vh = window.innerHeight;
    chains.forEach(({ track, steps }) => {
      const r = track.getBoundingClientRect();
      const startY = vh * 0.78;
      const endY = vh * 0.42;
      const total = r.height + (startY - endY);
      const p = Math.min(1, Math.max(0, (startY - r.top) / total));
      track.style.setProperty('--p', p.toFixed(3));
      const n = steps.length;
      steps.forEach((s, i) => {
        const at = n > 1 ? (i / (n - 1)) * 0.96 : 0;
        s.classList.toggle('is-on', p > at + 0.02 || (i === 0 && p > 0.02));
      });
    });
  };
  const onChainScroll = () => {
    if (chainTicking) return;
    chainTicking = true;
    requestAnimationFrame(updateChains);
  };
  const initChain = (scope) => {
    scope.querySelectorAll('[data-v-chain]').forEach((track) => {
      if (!once(track, 'vChain')) return;
      const steps = Array.from(track.querySelectorAll('[data-v-chain-step]'));
      if (reduce) {
        track.style.setProperty('--p', '1');
        steps.forEach((s) => s.classList.add('is-on'));
        return;
      }
      chains.push({ track, steps });
      if (chains.length === 1) {
        window.addEventListener('scroll', onChainScroll, { passive: true });
        window.addEventListener('resize', onChainScroll);
      }
      onChainScroll();
    });
  };

  /* --- Videos nur abspielen, wenn sichtbar ----------------------------- */
  const initVideo = (scope) => {
    scope.querySelectorAll('video[data-v-video]').forEach((video) => {
      if (!once(video, 'vVid')) return;
      video.muted = true;
      if (reduce) return;
      inView(
        video,
        (vis) => {
          if (vis) {
            if (video.preload === 'none') video.preload = 'auto';
            const p = video.play();
            if (p && p.catch) p.catch(() => {});
          } else {
            video.pause();
          }
        },
        { threshold: 0.25 }
      );
    });
  };

  /* --- Ledger: Eintraege nacheinander bestaetigen ---------------------- */
  const initLedger = (scope) => {
    scope.querySelectorAll('[data-v-ledger]').forEach((box) => {
      if (!once(box, 'vLedg')) return;
      const rows = Array.from(box.querySelectorAll('.vledg__row'));
      const count = box.querySelector('[data-v-count]');
      if (reduce || !rows.length) return;
      rows.forEach((r) => r.classList.remove('is-ok'));
      if (count) count.textContent = `0/${rows.length}`;
      let started = false;
      const io = inView(
        box,
        (vis) => {
          if (!vis || started) return;
          started = true;
          if (io) io.disconnect();
          rows.forEach((row, i) => {
            setTimeout(() => {
              row.classList.add('is-ok');
              if (count) count.textContent = `${i + 1}/${rows.length}`;
            }, 500 + i * 420);
          });
        },
        { threshold: 0.3 }
      );
    });
  };

  /* --- Kartenfaecher HODL or Trade ------------------------------------- */
  const initFan = (scope) => {
    scope.querySelectorAll('[data-v-fan]').forEach((fan) => {
      if (!once(fan, 'vFan') || reduce) return;
      fan.classList.remove('is-in');
      const io = inView(
        fan,
        (vis) => {
          if (!vis) return;
          setTimeout(() => fan.classList.add('is-in'), 150);
          if (io) io.disconnect();
        },
        { threshold: 0.35 }
      );
    });
  };

  /* --- Block-Navigation (rechts) --------------------------------------- */
  let railEl = null;
  let railObserver = null;
  const buildRail = () => {
    const blocks = Array.from(document.querySelectorAll('[data-v-block]')).filter((s) => s.id);
    if (railEl) railEl.remove();
    if (railObserver) railObserver.disconnect();
    if (blocks.length < 3) return;
    railEl = document.createElement('ul');
    railEl.className = 'v-blocks';
    railEl.setAttribute('aria-label', 'Abschnitte dieser Seite');
    const buttons = blocks.map((section, i) => {
      const li = document.createElement('li');
      const b = document.createElement('button');
      b.type = 'button';
      const name = section.dataset.vBlock;
      b.innerHTML = `<span>${String(i + 1).padStart(2, '0')} · ${name.replace(/[<>&]/g, '')}</span>`;
      b.setAttribute('aria-label', `Zu Abschnitt ${name}`);
      b.addEventListener('click', () => section.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' }));
      li.appendChild(b);
      railEl.appendChild(li);
      return b;
    });
    document.body.appendChild(railEl);

    railObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          const i = blocks.indexOf(e.target);
          buttons.forEach((b, k) => b.setAttribute('aria-current', String(k === i)));
        });
      },
      { rootMargin: '-45% 0px -50% 0px' }
    );
    blocks.forEach((b) => railObserver.observe(b));
  };
  const onRailScroll = () => {
    if (railEl) railEl.classList.toggle('is-visible', window.scrollY > window.innerHeight * 0.6);
  };
  window.addEventListener('scroll', onRailScroll, { passive: true });

  /* --- Start ------------------------------------------------------------ */
  const initAll = (scope) => {
    initReveal(scope);
    initScramble(scope);
    initTilt(scope);
    initNet(scope);
    initCards(scope);
    initMagnet(scope);
    initFinder(scope);
    initChain(scope);
    initVideo(scope);
    initLedger(scope);
    initFan(scope);
  };

  const boot = () => {
    initAll(document);
    buildRail();
    onRailScroll();
    html.classList.add('v-ready');
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', (e) => {
    initAll(e.target);
    buildRail();
  });
  document.addEventListener('shopify:section:unload', () => setTimeout(buildRail, 50));
})();
